import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Tooltip } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { CREATE_CLIENT_AUTHORIZATION, GET_SINGLE_ADFS_BY_BKS_ID } from '../administration/ADFSConfig/ADFSConfig.graphql';
import { GET_PORTAL_LOGIN } from './Sso.graphql';

interface Props {
  adfsId: string;
  scheme: string;
  bksID: number;
  onClose(): void;
}

type Claim = { type: string; value: string };

/** Every claim listed must be on the user for them to get in. */
export default function CustomClientRuleDialog({ adfsId, scheme, bksID, onClose }: Props) {
  const [clientId, setClientId] = useState('');
  const [claims, setClaims] = useState<Claim[]>([{ type: 'groups', value: '' }]);
  const [error, setError] = useState<string | null>(null);

  const [create, { loading }] = useMutation(CREATE_CLIENT_AUTHORIZATION, {
    refetchQueries: [
      { query: GET_PORTAL_LOGIN, variables: { bksID } },
      { query: GET_SINGLE_ADFS_BY_BKS_ID, variables: { bksID } },
    ],
    awaitRefetchQueries: true,
  });

  const setClaim = (index: number, change: Partial<Claim>) => setClaims((xs) => xs.map((x, i) => (i === index ? { ...x, ...change } : x)));
  const valid = clientId.trim() && claims.every((x) => x.type.trim() && x.value.trim());

  const save = async () => {
    setError(null);
    try {
      await create({
        variables: {
          input: {
            id: adfsId,
            scheme,
            clientName: clientId.trim(),
            // The mutation's own encoding: "type:value+type:value+".
            claimList: claims.map((x) => `${x.type.trim()}:${x.value.trim()}+`).join(''),
          },
        },
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Kunne ikke legge til regelen.');
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Egen regel for en klient</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="Klient-ID"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            helperText="Klienten slik den er registrert i Identity, f.eks. SalgClient."
            required
            fullWidth
            autoFocus
          />
          {claims.map((claim, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <TextField
                label="Claim-type"
                value={claim.type}
                onChange={(e) => setClaim(index, { type: e.target.value })}
                required
                sx={{ width: 160 }}
              />
              <TextField
                label="Verdi"
                value={claim.value}
                onChange={(e) => setClaim(index, { value: e.target.value })}
                required
                fullWidth
                slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
              />
              <Tooltip title="Fjern claim">
                <span>
                  <IconButton
                    aria-label="Fjern claim"
                    disabled={claims.length === 1}
                    onClick={() => setClaims((xs) => xs.filter((_, i) => i !== index))}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          ))}
          <Stack direction="row">
            <Button size="small" startIcon={<Add />} onClick={() => setClaims((xs) => [...xs, { type: 'groups', value: '' }])}>
              Legg til claim
            </Button>
          </Stack>
          {claims.length > 1 && <Alert severity="info">Brukeren må ha alle claimene for å slippe inn.</Alert>}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>
          Avbryt
        </Button>
        <Button variant="contained" disabled={!valid || loading} onClick={save}>
          Legg til
        </Button>
      </DialogActions>
    </Dialog>
  );
}
